<template>
  <div v-loading="loading" class="app-main-inner recon-compare-detail">
    <el-alert
      v-if="errorMessage"
      :title="errorMessage"
      type="error"
      show-icon
      :closable="false"
      class="compare-alert"
    />

    <template v-else>
      <div class="compare-page">
        <div class="compare-page__main">
          <div class="section compare-header">
            <div class="compare-header__meta">
              <div class="meta-item">
                <span>任务编号：</span>
                <strong>{{ taskDetail.taskNo || '-' }}</strong>
              </div>
              <div class="meta-item">
                <span>供应商：</span>
                <strong>{{ taskDetail.supplierName || '-' }}</strong>
              </div>
              <div class="meta-item">
                <span>账单类型：</span>
                <strong>{{ billTypeLabel }}</strong>
              </div>
            </div>

            <div class="compare-header__actions">
              <el-tooltip content="匹配完成后才可导出" :disabled="canExport" placement="top">
                <span class="compare-header__export">
                  <el-button
                    size="mini"
                    icon="el-icon-download"
                    :loading="exporting"
                    :disabled="!canExport"
                    @click="handleExportExcel"
                  >
                    导出 Excel
                  </el-button>
                </span>
              </el-tooltip>
              <el-button
                class="compare-header__save"
                type="primary"
                size="mini"
                :loading="saving"
                @click="handleSaveProgress"
              >
                保存进度
              </el-button>
            </div>
          </div>

          <div class="section compare-subheader">
            <div class="compare-subheader__filters">
              <el-radio-group v-model="activeFilter" size="mini" class="compare-filters">
                <el-radio-button
                  v-for="tab in filterTabs"
                  :key="tab.value"
                  :label="tab.value"
                >
                  {{ tab.label }} ({{ tab.count }})
                </el-radio-button>
              </el-radio-group>
              <el-select
                v-model="assigneeFilter"
                size="mini"
                clearable
                filterable
                placeholder="责任人"
                :class="['compare-subheader__assignee', { 'is-filtering': assigneeFilterActive }]"
              >
                <el-option
                  v-for="item in assigneeFilterOptions"
                  :key="item.userId"
                  :label="item.label"
                  :value="item.userId"
                />
              </el-select>
              <span v-if="assigneeFilterActive" class="compare-subheader__filter-hint">筛选中</span>
            </div>

            <div :class="['compare-tip', { 'is-filtering': assigneeFilterActive }]">
              <i class="el-icon-info" />
              <span>{{ compareTipText }}</span>
            </div>
          </div>

          <div v-if="!filteredRecords.length" class="compare-empty">
            <i class="el-icon-document-remove" />
            <p>{{ emptyRecordsText }}</p>
          </div>

          <div v-else class="record-list">
            <div
              v-for="record in filteredRecords"
              :key="record.recordUuid"
              :class="recordCardClass(record)"
            >
              <div class="record-card__head">
                <div class="record-card__identity">
                  <i class="el-icon-collection-tag" />
                  <strong>{{ record.blNo || record.businessNo || '-' }}</strong>
                  <el-tag :type="recordTagType(record)" size="mini" effect="plain">
                    {{ cardStatusLabel(record) }}
                  </el-tag>
                </div>

                <div class="record-card__head-side">
                  <div class="record-card__meta">系统单号：{{ recordSystemJobNo(record) }}</div>
                  <div :class="recordAmountClass(record)">总金额：{{ recordTotalText(record) }}</div>
                </div>
              </div>

              <div class="record-card__summary">
                <div class="summary-item summary-item--title">总费用比对</div>
                <div class="summary-item">
                  <span>供应商账单合计</span>
                  <strong>{{ sourceTotalText(record) }}</strong>
                </div>
                <div class="summary-item">
                  <span>系统数据合计</span>
                  <strong>{{ systemTotalText(record) }}</strong>
                </div>
                <div class="summary-item summary-item--status">
                  <strong><i :class="summaryIconClass(record)" /> {{ recordSummaryLabel(record) }}</strong>
                  <small>{{ recordSummaryHint(record) }}</small>
                  <el-tag
                    v-if="recordTotalAccepted(record)"
                    size="mini"
                    type="success"
                    effect="plain"
                    class="record-total-accept-tag"
                  >
                    已总额接受
                  </el-tag>
                  <el-button
                    v-else-if="canTotalAcceptRecord(record)"
                    size="mini"
                    type="primary"
                    plain
                    :loading="saving"
                    class="record-total-accept-btn"
                    @click.stop="handleTotalAccept(record)"
                  >
                    总额接受
                  </el-button>
                </div>
              </div>

              <el-alert
                v-if="recordDetailLoadFailed(record)"
                title="该提单明细加载失败，请刷新后重试；明细未加载成功前不能完成复核。"
                type="error"
                show-icon
                :closable="false"
                class="record-card__detail-error"
              />

              <div class="record-table">
                <div class="record-table__head">
                  <div>费用项</div>
                  <div>供应商账单</div>
                  <div>系统费用名称</div>
                  <div>系统数据</div>
                  <div>状态</div>
                  <div>责任人</div>
                  <div>操作</div>
                </div>

                <div
                  v-for="row in visibleComparisonRows(record)"
                  :key="row.rowKey"
                  :class="comparisonRowClass(record, row)"
                >
                  <div class="record-table__cell record-table__cell--name">
                    {{ row.supplierChargeName || row.systemChargeItemName || '-' }}
                  </div>
                  <div class="record-table__cell record-table__cell--amount">
                    {{ formatMoney(row.sourceAmount, row.currency) }}
                  </div>
                  <div
                    :class="[
                      'record-table__cell',
                      'record-table__cell--name',
                      { 'record-table__cell--emphasis': !row.supplierChargeName || !row.systemChargeItemName }
                    ]"
                  >
                    <span>{{ row.systemChargeItemName || '-' }}</span>
                    <el-tag
                      v-if="row.manualBound"
                      size="mini"
                      type="primary"
                      effect="plain"
                      class="manual-bind-tag"
                    >
                      人工关联
                    </el-tag>
                  </div>
                  <div
                    :class="[
                      'record-table__cell',
                      'record-table__cell--amount',
                      { 'record-table__cell--emphasis': !row.supplierChargeName || !row.systemChargeItemName || row.resultType === 'AMOUNT_DIFF' }
                    ]"
                  >
                    {{ formatMoney(row.systemAmount, row.currency) }}
                  </div>
                  <div class="record-table__cell">
                    <span :class="rowStatusClass(row)">{{ rowStatusLabel(row) }}</span>
                  </div>
                  <div class="record-table__cell record-table__cell--assignees">
                    <template v-if="rowAssignees(row).length">
                      <div
                        v-for="assignee in rowAssignees(row)"
                        :key="`${assignee.userId}-${assignee.roles || ''}`"
                        class="record-table__assignee"
                      >
                        {{ disputeAssigneeLabel(assignee) }}
                      </div>
                    </template>
                    <span v-else class="record-table__dash">—</span>
                  </div>
                  <div class="record-table__cell record-table__cell--actions">
                    <template v-if="rowActionText(row)">
                      <span :class="rowActionTextClass(row)">{{ rowActionText(row) }}</span>
                      <el-button
                        v-if="canBindSystemCharge(record, row)"
                        size="mini"
                        type="primary"
                        plain
                        @click="openBindSystemCharge(record, row)"
                      >
                        {{ bindActionLabel(row) }}
                      </el-button>
                    </template>
                    <template v-else>
                      <el-button
                        v-if="canBindSystemCharge(record, row)"
                        size="mini"
                        type="primary"
                        plain
                        @click="openBindSystemCharge(record, row)"
                      >
                        {{ bindActionLabel(row) }}
                      </el-button>
                      <template v-if="canOperateRow(row)">
                        <el-button
                          v-if="canForceAccept(row)"
                          size="mini"
                          type="primary"
                          plain
                          @click="openForceAccept(row)"
                        >
                          强制接受
                        </el-button>
                        <el-button
                          v-if="canDispute(row)"
                          size="mini"
                          type="warning"
                          plain
                          @click="openDispute(row)"
                        >
                          发起争议
                        </el-button>
                      </template>
                      <span
                        v-if="!canBindSystemCharge(record, row) && !canOperateRow(row)"
                        class="record-table__dash"
                      >
                        —
                      </span>
                    </template>
                  </div>
                </div>

                <div v-if="!visibleComparisonRows(record).length" class="record-table__empty">
                  {{ recordTableEmptyText(record) }}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <el-dialog
        title="强制接受"
        :visible.sync="forceAcceptDialogVisible"
        width="480px"
        append-to-body
        :close-on-click-modal="false"
        @closed="resetForceAccept"
      >
        <div class="compare-dialog__desc">
          确认强制接受此条费用差异吗？接受后该条目标记为“已强制接受”。
        </div>
        <el-form label-position="top" size="mini">
          <el-form-item label="费用项">
            <span>{{ forceContextText }}</span>
          </el-form-item>
          <el-form-item label="强制接受原因" required>
            <el-input
              v-model="forceAcceptForm.reason"
              type="textarea"
              :rows="4"
              resize="none"
              placeholder="请输入"
            />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button size="mini" @click="forceAcceptDialogVisible = false">取消</el-button>
          <el-button
            size="mini"
            type="primary"
            :disabled="!canSubmitForceAccept"
            @click="submitForceAccept"
          >
            确认接受
          </el-button>
        </div>
      </el-dialog>

      <el-dialog
        v-loading="disputeSubmitting"
        :element-loading-text="disputeSubmitProgressText"
        title="发起争议"
        :visible.sync="disputeDialogVisible"
        width="720px"
        append-to-body
        :close-on-click-modal="false"
        :close-on-press-escape="!disputeSubmitting"
        :show-close="!disputeSubmitting"
        :before-close="handleDisputeBeforeClose"
        @closed="resetDispute"
      >
        <div class="compare-dialog__desc">
          请选择一位处理人并填写争议原因，提交后系统将向该人员推送待办。
        </div>
        <el-form label-position="top" size="mini">
          <el-form-item label="推送给" required>
            <el-radio-group
              v-if="disputeAssigneeOptions.length"
              v-model="disputeForm.assigneeId"
              :disabled="disputeSubmitting"
            >
              <el-radio
                v-for="assignee in disputeAssigneeOptions"
                :key="assignee.userId"
                :label="assignee.userId"
              >
                {{ disputeAssigneeLabel(assignee) }}
              </el-radio>
            </el-radio-group>
            <span v-else class="compare-dialog__empty">当前费用和订单均未找到有效责任人，无法发起争议。</span>
          </el-form-item>
          <el-form-item label="争议原因说明" required>
            <el-input
              v-model="disputeForm.reason"
              type="textarea"
              :rows="4"
              resize="none"
              placeholder="请输入"
              :disabled="disputeSubmitting"
            />
          </el-form-item>
          <el-form-item v-if="disputeRelatedCandidates.length" class="dispute-related">
            <el-checkbox
              v-model="disputeForm.includeRelated"
              :disabled="disputeSubmitting"
              @change="handleDisputeIncludeRelatedChange"
            >
              同时提交该责任人的其他待争议费用（{{ disputeRelatedCandidates.length }}）
            </el-checkbox>
            <div v-if="disputeForm.includeRelated" class="dispute-related__list">
              <div class="dispute-related__toolbar">
                <el-checkbox
                  :value="disputeRelatedAllSelected"
                  :indeterminate="disputeRelatedIndeterminate"
                  :disabled="disputeSubmitting"
                  @change="handleDisputeIncludeRelatedChange"
                >
                  全选
                </el-checkbox>
              </div>
              <el-checkbox :value="true" disabled>
                {{ disputeBatchItemLabel(disputeTarget) }}（当前）
              </el-checkbox>
              <el-checkbox-group
                v-model="disputeForm.selectedKeys"
                :disabled="disputeSubmitting"
                @change="clearDisputeSubmitFailures"
              >
                <el-checkbox
                  v-for="item in disputeRelatedCandidates"
                  :key="item.row.rowKey"
                  :label="item.row.rowKey"
                >
                  {{ disputeBatchItemLabel(item) }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </el-form-item>
          <div v-if="disputeSubmitFailures.length" class="dispute-related__failures">
            <div>以下费用提交失败，可修改后重试：</div>
            <div
              v-for="item in disputeSubmitFailures"
              :key="item.rowKey"
              class="dispute-related__failure"
            >
              {{ item.label }}：{{ item.message }}
            </div>
          </div>
        </el-form>
        <div slot="footer">
          <el-button size="mini" :disabled="disputeSubmitting" @click="disputeDialogVisible = false">取消</el-button>
          <el-button
            size="mini"
            type="warning"
            :loading="disputeSubmitting"
            :disabled="!canSubmitDispute"
            @click="submitDispute"
          >
            {{ disputeSubmitButtonText }}
          </el-button>
        </div>
      </el-dialog>

      <el-dialog
        title="关联系统费用"
        :visible.sync="bindDialogVisible"
        width="520px"
        append-to-body
        :close-on-click-modal="false"
        @closed="resetBindSystemCharge"
      >
        <div class="compare-dialog__desc">
          从当前提单的系统费用快照中选择对应项。关联后会自动重新计算该行金额差额。
        </div>
        <el-form label-position="top" size="mini">
          <el-form-item label="供应商费用">
            <span>{{ bindContextText }}</span>
          </el-form-item>
          <el-form-item label="系统费用" required>
            <el-select
              v-model="bindForm.systemChargeSnapshotUuid"
              filterable
              placeholder="请选择系统费用"
              style="width: 100%"
            >
              <el-option
                v-for="item in bindCandidateOptions"
                :key="item.systemChargeSnapshotUuid"
                :label="systemChargeOptionLabel(item)"
                :value="item.systemChargeSnapshotUuid"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="关联说明">
            <el-input
              v-model="bindForm.reason"
              type="textarea"
              :rows="3"
              resize="none"
              maxlength="1000"
              show-word-limit
              placeholder="可选，说明费用名称对应关系"
            />
          </el-form-item>
        </el-form>
        <div slot="footer" class="bind-dialog__footer">
          <el-button
            v-if="canUnbindSystemCharge"
            size="mini"
            type="danger"
            plain
            :loading="saving"
            @click="submitUnbindSystemCharge"
          >
            解除当前匹配
          </el-button>
          <div class="bind-dialog__footer-actions">
            <el-button size="mini" @click="bindDialogVisible = false">取消</el-button>
            <el-button
              size="mini"
              type="primary"
              :disabled="!canSubmitBindSystemCharge"
              @click="submitBindSystemCharge"
            >
              确认关联
            </el-button>
          </div>
        </div>
      </el-dialog>

      <div class="compare-footer">
        <div class="compare-footer__progress">
          <span class="compare-footer__label">已处理进度</span>
          <el-progress
            class="compare-footer__progress-bar"
            :percentage="progressPercent"
            :stroke-width="8"
            :show-text="false"
          />
          <strong>{{ progressPercent }}%</strong>
        </div>

        <div class="compare-footer__diff">
          <span>差异总额：</span>
          <strong>{{ differenceTotalText }}</strong>
        </div>

        <div class="compare-footer__actions">
          <el-button size="mini" @click="backToTask">返回详情</el-button>
          <el-button
            size="mini"
            type="success"
            :disabled="!canCompleteReview"
            @click="handleComplete"
          >
            对账完成
          </el-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  bindMatchDetailSystemCharge,
  unbindMatchDetailSystemCharge,
  completeReconTask,
  disputeMatchDetail,
  exportReconComparison,
  forceAcceptMatchDetail,
  getMatchRecordDetail,
  getReconTaskDetail,
  listMatchRecords,
  preCheckComplete,
  totalAcceptMatchRecord
} from '@/api/clearing/aiReconciliation'
import ColumnUtils from '@/utils/ColumnUtils'
import convertResToBlob from '@/utils/convertResToBlob'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'

const BILL_TYPE_MAP = {
  FLEET: '干线费用',
  BOOKING: '订舱费用',
  CUSTOMS: '报关费用',
  FBA: '仓储费用',
  OVERSEAS_FLEET: '海外尾程',
  OTHER: '其他费用'
}

const STATUS_MAP = {
  MATCHED: { label: '已匹配', badgeClass: 'is-success', summaryClass: 'is-success' },
  SOURCE_ONLY: { label: '未匹配', badgeClass: 'is-danger', summaryClass: 'is-danger' },
  SYSTEM_ONLY: { label: '未匹配', badgeClass: 'is-danger', summaryClass: 'is-danger' },
  AMBIGUOUS: { label: '存在歧义', badgeClass: 'is-warning', summaryClass: 'is-warning' },
  DIFF: { label: '未匹配', badgeClass: 'is-danger', summaryClass: 'is-danger' },
  PENDING: { label: '待处理', badgeClass: 'is-info', summaryClass: 'is-info' }
}

const RESULT_TYPE_META = {
  MATCHED: { label: '已匹配', className: 'is-success' },
  SOURCE_ONLY: { label: '未匹配', className: 'is-danger' },
  SYSTEM_ONLY: { label: '供应商未提供', className: 'is-danger' },
  AMOUNT_DIFF: { label: '未匹配', className: 'is-warning' },
  NAME_DIFF: { label: '未匹配', className: 'is-warning' }
}

const COMPARISON_STATUSES = [
  'REVIEWING',
  'COMPLETING',
  'COMPLETED',
  'PARTIALLY_COMPLETED'
]

export default {
  name: 'TabReconciliationHistory',
  props: {
    taskId: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      saving: false,
      exporting: false,
      completing: false,
      errorMessage: '',
      activeFilter: 'ALL',
      assigneeFilter: '',
      taskDetail: {},
      records: [],
      detailMap: {},
      draftStateMap: {},
      forceAcceptDialogVisible: false,
      disputeDialogVisible: false,
      disputeSubmitting: false,
      disputeSubmitProgress: {
        current: 0,
        total: 0
      },
      disputeSubmitFailures: [],
      bindDialogVisible: false,
      forceAcceptForm: {
        targetKey: '',
        reason: ''
      },
      disputeForm: {
        targetKey: '',
        reason: '',
        assigneeId: '',
        includeRelated: false,
        selectedKeys: []
      },
      bindForm: {
        targetKey: '',
        systemChargeSnapshotUuid: '',
        reason: ''
      }
    }
  },
  computed: {
    billTypeLabel() {
      return BILL_TYPE_MAP[this.taskDetail.billType] || this.taskDetail.billType || '-'
    },
    canExport() {
      return COMPARISON_STATUSES.includes(this.taskDetail.status)
    },
    assigneeFilterActive() {
      return this.assigneeFilter !== '' && this.assigneeFilter != null
    },
    compareTipText() {
      if (this.assigneeFilterActive) {
        return '已按责任人筛选，仅展示该责任人的费用行；提单卡片合计仍为整单金额'
      }
      return '左右对照：左侧为供应商上传数据，右侧为系统业务数据'
    },
    filterTabs() {
      return [
        { value: 'ALL', label: '全部提单', count: this.records.length },
        { value: 'MATCHED', label: '已匹配', count: this.records.filter(item => this.recordIsReconciled(item)).length },
        { value: 'UNMATCHED', label: '未匹配', count: this.records.filter(item => !this.recordIsReconciled(item) && !this.recordIsSourceOnly(item)).length },
        { value: 'SOURCE_ONLY', label: '未对应', count: this.records.filter(item => this.recordIsSourceOnly(item)).length }
      ]
    },
    filteredRecords() {
      let list = this.records
      if (this.activeFilter === 'MATCHED') {
        list = this.records.filter(item => this.recordIsReconciled(item))
      } else if (this.activeFilter === 'UNMATCHED') {
        list = this.records.filter(item => !this.recordIsReconciled(item) && !this.recordIsSourceOnly(item))
      } else if (this.activeFilter === 'SOURCE_ONLY') {
        list = this.records.filter(item => this.recordIsSourceOnly(item))
      }
      if (this.assigneeFilter) {
        return list.filter(record => this.visibleComparisonRows(record).length > 0)
      }
      return list
    },
    emptyRecordsText() {
      if (this.assigneeFilter) {
        return '当前责任人没有可展示的费用详情'
      }
      return '暂无可展示的提单比对结果'
    },
    assigneeFilterOptions() {
      const map = {}
      this.allComparisonRows.forEach(row => {
        (row.disputeAssignees || []).forEach(assignee => {
          const userId = assignee && assignee.userId
          if (userId == null || userId === '') return
          const key = String(userId)
          if (!map[key]) {
            map[key] = { userId, roleSet: [] }
          }
          String(assignee.roles || '').split(',').forEach(role => {
            const trimmed = role.trim()
            if (trimmed && map[key].roleSet.indexOf(trimmed) === -1) {
              map[key].roleSet.push(trimmed)
            }
          })
        })
      })
      return Object.keys(map).map(key => {
        const item = map[key]
        return {
          userId: item.userId,
          label: this.disputeAssigneeLabel({
            userId: item.userId,
            roles: item.roleSet.join(',')
          })
        }
      }).sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'))
    },
    allComparisonRows() {
      return this.records.reduce((result, record) => {
        return result.concat(this.comparisonRows(record))
      }, [])
    },
    resolvedDraftCount() {
      return this.records.reduce((count, record) => {
        const rows = this.comparisonRows(record).filter(row => this.canOperateRow(row))
        if (this.recordTotalAccepted(record)) {
          return count + rows.length
        }
        return count + rows.filter(row => !!this.rowDraft(row)).length
      }, 0)
    },
    progressPercent() {
      const total = this.numberValue(this.taskDetail.totalItemCount) || this.allComparisonRows.length
      if (!total) return 0
      const matched = this.numberValue(this.taskDetail.matchedCount) || this.allComparisonRows.filter(row => row.resultType === 'MATCHED').length
      const progress = ((matched + this.resolvedDraftCount) / total) * 100
      return Math.max(0, Math.min(100, Math.round(progress)))
    },
    pendingRowCount() {
      return this.records.reduce((count, record) => {
        if (this.recordTotalAccepted(record)) {
          return count
        }
        return count + this.comparisonRows(record)
          .filter(row => this.canOperateRow(row) && !this.rowDraft(row)).length
      }, 0)
    },
    canCompleteReview() {
      return !!this.records.length && !this.hasDetailLoadFailed && this.pendingRowCount === 0
    },
    hasDetailLoadFailed() {
      return this.records.some(record => this.recordDetailLoadFailed(record))
    },
    differenceTotalText() {
      const totals = this.records.reduce((result, record) => {
        result.cny += Math.abs(this.numberValue(record.cnyDiffAmount))
        result.usd += Math.abs(this.numberValue(record.usdDiffAmount))
        return result
      }, { cny: 0, usd: 0 })
      return this.formatDualAmount(totals.cny, totals.usd)
    },
    forceTarget() {
      return this.findRowContext(this.forceAcceptForm.targetKey)
    },
    disputeTarget() {
      return this.findRowContext(this.disputeForm.targetKey)
    },
    bindTarget() {
      return this.findRowContext(this.bindForm.targetKey)
    },
    forceContextText() {
      if (!this.forceTarget) return '请选择左侧需要强制接受的费用项。'
      const { row } = this.forceTarget
      return `${this.rowDisplayName(row)} ｜ ${this.rowStatusLabel(row)}`
    },
    disputeAssigneeOptions() {
      if (!this.disputeTarget) return []
      return this.disputeTarget.row.disputeAssignees || []
    },
    disputeRelatedCandidates() {
      if (!this.disputeTarget || !this.disputeForm.assigneeId) return []
      const currentKey = this.disputeForm.targetKey
      const assigneeId = this.disputeForm.assigneeId
      const result = []
      this.records.forEach(record => {
        this.comparisonRows(record).forEach(row => {
          if (row.rowKey === currentKey || !row.detailUuid || !this.canDispute(row)) return
          if (!this.rowHasAssignee(row, assigneeId)) return
          result.push({ record, row })
        })
      })
      return result
    },
    disputeRelatedAllSelected() {
      return !!this.disputeRelatedCandidates.length &&
        this.disputeRelatedCandidates.every(item => this.disputeForm.selectedKeys.indexOf(item.row.rowKey) !== -1)
    },
    disputeRelatedIndeterminate() {
      const selectedCount = this.disputeRelatedCandidates.filter(item => {
        return this.disputeForm.selectedKeys.indexOf(item.row.rowKey) !== -1
      }).length
      return selectedCount > 0 && selectedCount < this.disputeRelatedCandidates.length
    },
    disputeItemsToSubmit() {
      if (this.disputeSubmitFailures.length) {
        return this.disputeSubmitFailures
          .map(item => this.findRowContext(item.rowKey))
          .filter(item => item && item.row && item.row.detailUuid && this.canDispute(item.row))
      }
      const items = []
      if (this.disputeTarget && this.disputeTarget.row.detailUuid && this.canDispute(this.disputeTarget.row)) {
        items.push(this.disputeTarget)
      }
      if (this.disputeForm.includeRelated) {
        this.disputeRelatedCandidates.forEach(item => {
          if (this.disputeForm.selectedKeys.indexOf(item.row.rowKey) !== -1) {
            items.push(item)
          }
        })
      }
      return items
    },
    disputeSubmitProgressText() {
      const { current, total } = this.disputeSubmitProgress
      if (!total) return '正在提交争议'
      return `正在提交 ${current}/${total}`
    },
    disputeSubmitButtonText() {
      return this.disputeSubmitFailures.length ? '重试失败项' : '提交争议'
    },
    bindContextText() {
      if (!this.bindTarget) return '请选择左侧需要关联的供应商费用项。'
      const { row } = this.bindTarget
      return `${this.rowDisplayName(row)} ｜ ${this.formatMoney(row.sourceAmount, row.currency)}`
    },
    bindCandidateOptions() {
      if (!this.bindTarget) return []
      const { record, row } = this.bindTarget
      return this.bindCandidateOptionsFor(record, row)
    },
    canSubmitForceAccept() {
      return !!this.forceTarget && !!this.forceTarget.row.detailUuid && !!this.forceAcceptForm.reason.trim()
    },
    canSubmitDispute() {
      return !this.disputeSubmitting && !!this.disputeForm.reason.trim() &&
        this.disputeAssigneeOptions.some(item => item.userId === this.disputeForm.assigneeId) &&
        this.disputeItemsToSubmit.length > 0
    },
    canSubmitBindSystemCharge() {
      return !!this.bindTarget && !!this.bindTarget.row.detailUuid &&
        this.bindCandidateOptions.some(item => item.systemChargeSnapshotUuid === this.bindForm.systemChargeSnapshotUuid)
    },
    canUnbindSystemCharge() {
      return !!this.bindTarget && !!this.bindTarget.row.detailUuid &&
        this.bindTarget.row.manualBound && this.bindTarget.row.reviewStatus === 'PENDING'
    }
  },
  watch: {
    taskId: {
      immediate: true,
      handler() {
        this.assigneeFilter = ''
        this.fetchPage()
      }
    },
    'disputeForm.assigneeId'() {
      if (!this.disputeDialogVisible) return
      this.disputeSubmitFailures = []
      this.syncDisputeRelatedSelection()
    }
  },
  methods: {
    async fetchPage(options) {
      if (!this.taskId) {
        this.errorMessage = '缺少任务标识，无法加载对比详情'
        return
      }

      const silent = !!(options && options.silent)
      if (!silent) this.loading = true
      this.errorMessage = ''
      this.restoreDraftState()

      try {
        const [taskRes, recordsRes] = await Promise.all([
          getReconTaskDetail(this.taskId),
          listMatchRecords(this.taskId)
        ])

        this.taskDetail = (taskRes && taskRes.data) || {}
        this.records = ((recordsRes && recordsRes.data) || []).map(item => this.normalizeRecord(item))

        const detailEntries = await Promise.all(this.records.map(async record => {
          try {
            const detailRes = await getMatchRecordDetail(record.recordUuid)
            return [record.recordUuid, this.normalizeDetail(detailRes && detailRes.data)]
          } catch (error) {
            return [record.recordUuid, this.createFailedDetail(record)]
          }
        }))

        const detailMap = {}
        detailEntries.forEach(([recordUuid, detail]) => {
          detailMap[recordUuid] = detail
        })
        this.detailMap = detailMap
        this.sanitizeDraftState()
      } catch (error) {
        if (silent) {
          this.$message.error('比对详情刷新失败，请稍后手动刷新')
          return
        }
        this.taskDetail = {}
        this.records = []
        this.detailMap = {}
        this.errorMessage = '比对详情加载失败，请稍后重试'
      } finally {
        if (!silent) this.loading = false
      }
    },
    normalizeRecord(item) {
      const statusMeta = STATUS_MAP[item.status] || STATUS_MAP.PENDING
      return Object.assign({}, item, {
        statusLabel: statusMeta.label,
        badgeClass: statusMeta.badgeClass,
        summaryClass: statusMeta.summaryClass,
        acceptStatus: item.acceptStatus || 'NONE',
        reconciled: item.reconciled === true
      })
    },
    normalizeDetail(detail) {
      const data = detail || {}
      const comparisonItems = (data.comparisonItems || []).map((item, index) => {
        return Object.assign({}, item, {
          rowKey: item.detailUuid || [
            data.recordUuid || '',
            index,
            item.resultType || '',
            item.supplierChargeName || '',
            item.systemChargeItemName || ''
          ].join(':')
        })
      })

      return Object.assign({
        recordUuid: '',
        status: '',
        operatorId: '',
        detailLoadFailed: false,
        supplierItems: [],
        systemItems: [],
        comparisonItems: []
      }, data, { comparisonItems })
    },
    createFailedDetail(record) {
      return {
        recordUuid: record.recordUuid,
        status: record.status,
        operatorId: '',
        detailLoadFailed: true,
        supplierItems: [],
        systemItems: [],
        comparisonItems: []
      }
    },
    comparisonRows(record) {
      const detail = this.detailMap[record.recordUuid] || {}
      return detail.comparisonItems || []
    },
    visibleComparisonRows(record) {
      const rows = this.comparisonRows(record)
      if (!this.assigneeFilter) return rows
      return rows.filter(row => this.rowHasAssignee(row, this.assigneeFilter))
    },
    rowAssignees(row) {
      return (row.disputeAssignees || []).filter(item => item && item.userId)
    },
    rowHasAssignee(row, userId) {
      return this.rowAssignees(row).some(item => String(item.userId) === String(userId))
    },
    recordTableEmptyText(record) {
      if (this.assigneeFilter) {
        return '当前责任人没有可展示的费用明细'
      }
      return this.recordDetailLoadFailed(record) ? '费用明细加载失败' : '暂无费用明细'
    },
    recordDetail(record) {
      return this.detailMap[record.recordUuid] || {}
    },
    recordDetailLoadFailed(record) {
      return !!this.recordDetail(record).detailLoadFailed
    },
    recordSystemJobNo(record) {
      return this.recordDetail(record).systemJobNo || '-'
    },
    recordOperatorLabel(record) {
      return this.recordDetail(record).operatorId || '待指派'
    },
    recordReason(record) {
      const detail = this.recordDetail(record)
      return detail.resultReason || record.resultReason || ''
    },
    recordCardClass(record) {
      return [
        'section',
        'record-card',
        !this.recordIsReconciled(record) ? 'record-card--alert' : 'record-card--matched',
        record.status === 'SOURCE_ONLY' && !this.recordIsReconciled(record) ? 'record-card--invalid' : '',
        this.isActiveRecord(record) ? 'record-card--active' : ''
      ]
    },
    recordTagType(record) {
      return (record.badgeClass || 'is-info').replace('is-', '')
    },
    recordAmountClass(record) {
      return [
        'record-card__amount',
        this.recordIsReconciled(record) ? 'record-card__amount--success' : 'record-card__amount--danger'
      ]
    },
    cardStatusLabel(record) {
      if (this.recordIsReconciled(record)) return '已匹配'
      if (record.status === 'SOURCE_ONLY') return '系统无对应'
      return record.statusLabel || '待处理'
    },
    sourceTotalText(record) {
      const detail = this.recordDetail(record)
      return this.formatDualAmount(
        this.pickNumber(detail.sourceCnyAmount, record.sourceCnyAmount),
        this.pickNumber(detail.sourceUsdAmount, record.sourceUsdAmount)
      )
    },
    systemTotalText(record) {
      const detail = this.recordDetail(record)
      return this.formatDualAmount(
        this.pickNumber(detail.systemCnyAmount, record.systemCnyAmount),
        this.pickNumber(detail.systemUsdAmount, record.systemUsdAmount)
      )
    },
    recordTotalText(record) {
      const detail = this.recordDetail(record)
      const cny = this.pickNumber(
        detail.systemCnyAmount,
        detail.sourceCnyAmount,
        record.systemCnyAmount,
        record.sourceCnyAmount
      )
      const usd = this.pickNumber(
        detail.systemUsdAmount,
        detail.sourceUsdAmount,
        record.systemUsdAmount,
        record.sourceUsdAmount
      )
      return this.formatDualAmount(cny, usd)
    },
    summaryIconClass(record) {
      if (this.recordIsReconciled(record)) return 'el-icon-success is-success'
      if (record.status === 'SOURCE_ONLY') return 'el-icon-circle-close is-info'
      if (record.status === 'AMBIGUOUS') return 'el-icon-warning-outline is-warning'
      return 'el-icon-warning is-danger'
    },
    recordSummaryLabel(record) {
      if (this.recordDetailLoadFailed(record)) return '明细加载失败'
      if (this.recordIsReconciled(record)) {
        if (this.recordTotalAccepted(record)) return '已匹配（总额接受）'
        if (record.status === 'MATCHED') return '完全一致'
        return '已匹配'
      }
      if (record.status === 'SOURCE_ONLY') return '系统无对应业务'
      if (this.canTotalAcceptRecord(record)) return '总额一致'
      if (record.status === 'AMBIGUOUS') return '存在匹配歧义'
      return '存在费用差异'
    },
    recordSummaryHint(record) {
      if (this.recordDetailLoadFailed(record)) return '请刷新重试，不能按空明细完成复核'
      if (this.recordIsReconciled(record)) {
        if (this.recordTotalAccepted(record)) return '总额一致，明细差异已按整组接受'
        if (record.status === 'MATCHED') return '供应商与系统费用一致'
        return '费用均已匹配或强制接受，可参与对账完成'
      }
      if (record.status === 'SOURCE_ONLY') return '该提单/业务在系统中不存在，无需人工比对'
      if (this.canTotalAcceptRecord(record)) return '总额一致但明细存在差异，可直接按总额接受'
      const diffText = this.formatDualAmount(
        Math.abs(this.numberValue(record.cnyDiffAmount)),
        Math.abs(this.numberValue(record.usdDiffAmount))
      )
      return `差异 ${diffText}`
    },
    recordHandled(record) {
      if (this.recordDetailLoadFailed(record)) return false
      if (this.recordTotalAccepted(record)) return true
      return this.comparisonRows(record)
        .filter(row => this.canOperateRow(row))
        .every(row => !!this.rowDraft(row))
    },
    recordIsReconciled(record) {
      return !!record && record.reconciled === true
    },
    recordIsSourceOnly(record) {
      return !!record && record.status === 'SOURCE_ONLY'
    },
    recordTotalAccepted(record) {
      return record && record.acceptStatus === 'TOTAL_ACCEPTED'
    },
    canTotalAcceptRecord(record) {
      if (!record || this.recordDetailLoadFailed(record) || this.recordTotalAccepted(record)) return false
      if (!this.isRecordTotalAmountMatched(record)) return false
      return this.comparisonRows(record).some(row => this.canOperateRow(row) && !this.rowDraft(row))
    },
    isRecordTotalAmountMatched(record) {
      return Math.abs(this.numberValue(record.cnyDiffAmount)) === 0 &&
        Math.abs(this.numberValue(record.usdDiffAmount)) === 0
    },
    comparisonRowClass(record, row) {
      return [
        'record-table__row',
        row.resultType === 'MATCHED' ? 'record-table__row--matched' : 'record-table__row--alert',
        this.isSelectedRow(row) ? 'record-table__row--active' : '',
        this.rowDraft(row) ? 'record-table__row--handled' : ''
      ]
    },
    rowDisplayName(row) {
      return row.supplierChargeName || row.systemChargeItemName || '费用项'
    },
    rowStatusLabel(row) {
      const draft = this.rowDraft(row)
      if (draft && draft.action === 'FORCE_ACCEPT') return '已强制接受'
      if (draft && draft.action === 'DISPUTED') return '争议中'
      return (RESULT_TYPE_META[row.resultType] || RESULT_TYPE_META.NAME_DIFF || {}).label || '待处理'
    },
    rowStatusClass(row) {
      const draft = this.rowDraft(row)
      if (draft && draft.action === 'FORCE_ACCEPT') return 'row-status row-status--warning'
      if (draft && draft.action === 'DISPUTED') return 'row-status row-status--info'
      const meta = RESULT_TYPE_META[row.resultType] || RESULT_TYPE_META.NAME_DIFF
      return ['row-status', `row-status--${meta.className || 'is-info'}`]
    },
    rowActionText(row) {
      const draft = this.rowDraft(row)
      if (!draft) return ''
      if (draft.action === 'FORCE_ACCEPT') return draft.reason || '已强制接受'
      if (draft.action === 'DISPUTED') return draft.reason || '已提交争议'
      return ''
    },
    rowActionTextClass(row) {
      const draft = this.rowDraft(row)
      if (draft && draft.action === 'FORCE_ACCEPT') return 'row-action-text row-action-text--warning'
      if (draft && draft.action === 'DISPUTED') return 'row-action-text row-action-text--info'
      return 'row-action-text'
    },
    canOperateRow(row) {
      return row.resultType && row.resultType !== 'MATCHED' && row.resultType !== 'SOURCE_ONLY'
    },
    canForceAccept(row) {
      // 强制接受会立即锁定已关联的系统费用；未关联费用没有可锁定的费用 ID。
      return row.reviewStatus === 'PENDING' && this.canOperateRow(row) && !!row.systemChargeSnapshotUuid
    },
    canDispute(row) {
      // 未匹配费用可通过所属提单的订单操作/商务发起争议，无需已关联系统费用。
      return row.reviewStatus === 'PENDING' && this.canOperateRow(row)
    },
    bindCandidateOptionsFor(record, row) {
      if (!record || !row) return []
      const usedSnapshotUuids = this.comparisonRows(record)
        // SYSTEM_ONLY 是“系统有、供应商未提供”的待处理行：可被供应商未匹配行人工消费。
        // 其他已经绑定到供应商费用的系统快照仍不能重复选择。
        .filter(item => item.detailUuid !== row.detailUuid &&
          item.systemChargeSnapshotUuid && item.resultType !== 'SYSTEM_ONLY')
        .map(item => item.systemChargeSnapshotUuid)
      return (this.recordDetail(record).systemItems || []).filter(item => {
        return item.systemChargeSnapshotUuid &&
          this.sameCurrency(item.currency, row.currency) &&
          usedSnapshotUuids.indexOf(item.systemChargeSnapshotUuid) === -1
      })
    },
    canBindSystemCharge(record, row) {
      if (!record || !row || this.recordDetailLoadFailed(record) || this.recordTotalAccepted(record)) return false
      if (row.reviewStatus !== 'PENDING') return false
      if ((!row.manualBound && row.resultType !== 'SOURCE_ONLY' && row.resultType !== 'NAME_DIFF') || !row.detailUuid || !row.supplierChargeName) return false
      return this.bindCandidateOptionsFor(record, row).length > 0
    },
    bindActionLabel(row) {
      return row && row.manualBound ? '修改关联' : '关联费用'
    },
    rowDraft(row) {
      if (row.reviewStatus === 'FORCE_ACCEPTED') {
        return { action: 'FORCE_ACCEPT', reason: row.reviewReason || '' }
      }
      if (row.reviewStatus === 'DISPUTED') {
        return { action: 'DISPUTED', reason: row.reviewReason || '' }
      }
      return this.draftStateMap[row.rowKey] || null
    },
    findRowContext(targetKey) {
      if (!targetKey) return null
      for (const record of this.records) {
        const row = this.comparisonRows(record).find(item => item.rowKey === targetKey)
        if (row) {
          return { record, row }
        }
      }
      return null
    },
    isSelectedRow(row) {
      return row.rowKey === this.forceAcceptForm.targetKey ||
        row.rowKey === this.disputeForm.targetKey || row.rowKey === this.bindForm.targetKey
    },
    isActiveRecord(record) {
      const activeKeys = [
        this.forceAcceptForm.targetKey,
        this.disputeForm.targetKey,
        this.bindForm.targetKey
      ].filter(Boolean)
      return activeKeys.some(targetKey => {
        const context = this.findRowContext(targetKey)
        return context && context.record.recordUuid === record.recordUuid
      })
    },
    openForceAccept(row) {
      const draft = this.rowDraft(row)
      this.forceAcceptForm.targetKey = row.rowKey
      this.forceAcceptForm.reason = draft && draft.action === 'FORCE_ACCEPT' ? draft.reason : ''
      this.forceAcceptDialogVisible = true
    },
    openDispute(row) {
      const draft = this.rowDraft(row)
      this.disputeSubmitFailures = []
      this.disputeSubmitProgress = { current: 0, total: 0 }
      this.disputeForm = {
        targetKey: row.rowKey,
        reason: draft && draft.action === 'DISPUTED' ? draft.reason : '',
        assigneeId: (row.disputeAssignees || []).length ? row.disputeAssignees[0].userId : '',
        includeRelated: false,
        selectedKeys: []
      }
      this.disputeDialogVisible = true
    },
    openBindSystemCharge(record, row) {
      this.bindForm.targetKey = row.rowKey
      // 修改关联时必须回显当前绑定项，否则用户无法判断现有关联关系。
      this.bindForm.systemChargeSnapshotUuid = row.systemChargeSnapshotUuid || ''
      this.bindForm.reason = row.manualBoundReason || ''
      this.bindDialogVisible = true
    },
    async submitForceAccept() {
      if (!this.canSubmitForceAccept) return
      const { row } = this.forceTarget
      this.saving = true
      try {
        await forceAcceptMatchDetail(row.detailUuid, this.forceAcceptForm.reason.trim())
        this.forceAcceptDialogVisible = false
        this.resetForceAccept()
        await this.fetchPage()
        this.$message.success('已强制接受')
      } finally {
        this.saving = false
      }
    },
    async submitDispute() {
      if (!this.canSubmitDispute) return
      const items = this.disputeItemsToSubmit.slice()
      if (!items.length) return
      const reason = this.disputeForm.reason.trim()
      const assigneeId = this.disputeForm.assigneeId
      this.disputeSubmitting = true
      this.disputeSubmitFailures = []
      this.disputeSubmitProgress = { current: 0, total: items.length }
      try {
        for (let index = 0; index < items.length; index++) {
          this.disputeSubmitProgress.current = index + 1
          const item = items[index]
          try {
            await disputeMatchDetail(item.row.detailUuid, { reason, assigneeId }, true)
          } catch (error) {
            this.disputeSubmitFailures.push({
              rowKey: item.row.rowKey,
              label: this.disputeBatchItemLabel(item),
              message: this.disputeErrorMessage(error)
            })
          }
        }
        const successCount = items.length - this.disputeSubmitFailures.length
        await this.fetchPage({ silent: true })
        if (!this.disputeSubmitFailures.length) {
          this.disputeDialogVisible = false
          this.resetDispute()
          this.$message.success(`已提交 ${items.length} 条`)
          return
        }
        if (successCount) {
          this.$message.warning(`已提交 ${successCount} 条，${this.disputeSubmitFailures.length} 条失败`)
        } else {
          this.$message.error(`${this.disputeSubmitFailures.length} 条提交失败`)
        }
      } finally {
        this.disputeSubmitting = false
      }
    },
    handleDisputeBeforeClose(done) {
      if (this.disputeSubmitting) return
      done()
    },
    clearDisputeSubmitFailures() {
      this.disputeSubmitFailures = []
    },
    handleDisputeIncludeRelatedChange(checked) {
      this.disputeSubmitFailures = []
      this.disputeForm.selectedKeys = checked
        ? this.disputeRelatedCandidates.map(item => item.row.rowKey)
        : []
    },
    syncDisputeRelatedSelection() {
      if (!this.disputeRelatedCandidates.length) {
        this.disputeForm.includeRelated = false
        this.disputeForm.selectedKeys = []
        return
      }
      if (this.disputeForm.includeRelated) {
        this.disputeForm.selectedKeys = this.disputeRelatedCandidates.map(item => item.row.rowKey)
      } else {
        this.disputeForm.selectedKeys = []
      }
    },
    disputeBatchItemLabel(item) {
      if (!item || !item.row) return '-'
      const record = item.record || {}
      const row = item.row
      const blNo = record.blNo || record.businessNo || '-'
      return `${blNo} ｜ ${this.rowDisplayName(row)} ｜ ${this.formatMoney(row.sourceAmount || row.systemAmount, row.currency)} ｜ ${this.rowStatusLabel(row)}`
    },
    disputeErrorMessage(error) {
      if (!error) return '提交失败'
      if (typeof error === 'string') return error
      return error.errorMsg || error.message || error.msg || '提交失败'
    },
    async submitBindSystemCharge() {
      if (!this.canSubmitBindSystemCharge) return
      const { row } = this.bindTarget
      this.saving = true
      try {
        await bindMatchDetailSystemCharge(row.detailUuid, {
          systemChargeSnapshotUuid: this.bindForm.systemChargeSnapshotUuid,
          reason: this.bindForm.reason.trim()
        })
        this.bindDialogVisible = false
        this.resetBindSystemCharge()
        await this.fetchPage()
        this.$message.success('已关联系统费用，已重新计算差额')
      } finally {
        this.saving = false
      }
    },
    submitUnbindSystemCharge() {
      if (!this.canUnbindSystemCharge) return
      const { row } = this.bindTarget
      this.$confirm('解除后，该供应商费用会恢复为待匹配，系统费用会显示为“供应商未提供”。是否继续？', '解除当前匹配', {
        type: 'warning'
      }).then(async() => {
        this.saving = true
        try {
          await unbindMatchDetailSystemCharge(row.detailUuid)
          this.bindDialogVisible = false
          this.resetBindSystemCharge()
          await this.fetchPage()
          this.$message.success('已解除当前匹配')
        } finally {
          this.saving = false
        }
      }).catch(() => {})
    },
    handleTotalAccept(record) {
      if (!this.canTotalAcceptRecord(record)) return
      this.$confirm('该提单/业务分组总额一致，确认按总额接受本组明细差异？', '总额接受', { type: 'warning' })
        .then(async() => {
          this.saving = true
          try {
            await totalAcceptMatchRecord(record.recordUuid, '总额一致，按总额接受')
            await this.fetchPage()
            this.$message.success('已总额接受')
          } finally {
            this.saving = false
          }
        })
        .catch(() => {})
    },
    resetForceAccept() {
      this.forceAcceptForm = {
        targetKey: '',
        reason: ''
      }
    },
    resetDispute() {
      this.disputeForm = {
        targetKey: '',
        reason: '',
        assigneeId: '',
        includeRelated: false,
        selectedKeys: []
      }
      this.disputeSubmitting = false
      this.disputeSubmitProgress = { current: 0, total: 0 }
      this.disputeSubmitFailures = []
    },
    resetBindSystemCharge() {
      this.bindForm = {
        targetKey: '',
        systemChargeSnapshotUuid: '',
        reason: ''
      }
    },
    handleSaveProgress() {
      this.saving = true
      try {
        this.persistDraftState(true)
        this.$message.success('已保存本地进度')
      } finally {
        this.saving = false
      }
    },
    handleExportExcel() {
      if (!this.canExport || this.exporting || !this.taskDetail.taskUuid) return
      this.exporting = true
      exportReconComparison(this.taskDetail.taskUuid)
        .then(res => {
          convertResToBlob(res)
          this.$message.success('导出成功')
        })
        .catch(err => {
          if (err && err.response) return
          this.$message.error(typeof err === 'string' ? err : '导出失败，请稍后重试')
        })
        .finally(() => {
          this.exporting = false
        })
    },
    handleComplete() {
      if (!this.canCompleteReview) return
      this.completing = true
      preCheckComplete(this.taskDetail.taskUuid)
        .then(res => {
          const data = (res && res.data) || {}
          if (!data.canComplete) {
            this.$message.warning(data.message || '无法完成对账')
            return
          }
          const lockingCount = data.lockingCount === undefined || data.lockingCount === null
            ? data.completingCount
            : data.lockingCount
          const confirmMsg = data.pendingExceptionCount > 0
            ? `还有 ${data.pendingExceptionCount} 条未处理异常不参与本次结算，确认完成对账？`
            : lockingCount > 0
              ? `确认完成对账？将锁定 ${lockingCount} 条未锁定费用并生成账单`
              : '确认完成对账？费用已在强制接受时锁定，将生成账单'
          this.$confirm(confirmMsg, '提示', { type: 'warning' })
            .then(() => this.doComplete())
            .catch(() => {})
        })
        .finally(() => { this.completing = false })
    },
    doComplete() {
      this.completing = true
      completeReconTask(this.taskDetail.taskUuid)
        .then(res => {
          const data = (res && res.data) || {}
          this.$message.success(data.message || '对账完成')
          this.fetchPage()
        })
        .finally(() => { this.completing = false })
    },
    backToTask() {
      this.$emit('view-task', {
        taskUuid: this.taskId,
        taskNo: this.taskDetail.taskNo
      })
    },
    draftStorageKey() {
      return `ai-reconciliation-compare:${this.taskId}`
    },
    restoreDraftState() {
      if (typeof window === 'undefined' || !window.localStorage || !this.taskId) return
      try {
        const raw = window.localStorage.getItem(this.draftStorageKey())
        if (!raw) return
        const parsed = JSON.parse(raw)
        this.draftStateMap = {}
        this.activeFilter = parsed.activeFilter || 'ALL'
      } catch (error) {
        this.draftStateMap = {}
      }
    },
    persistDraftState(quiet = false) {
      if (typeof window === 'undefined' || !window.localStorage || !this.taskId) return
      window.localStorage.setItem(this.draftStorageKey(), JSON.stringify({
        activeFilter: this.activeFilter
      }))
      if (!quiet) {
        this.$message.success('已保存本地进度')
      }
    },
    sanitizeDraftState() {
      const rowMap = this.allComparisonRows.reduce((result, row) => {
        result[row.rowKey] = row
        return result
      }, {})
      this.draftStateMap = {}
      if (!rowMap[this.forceAcceptForm.targetKey]) this.resetForceAccept()
      if (!this.disputeDialogVisible && !rowMap[this.disputeForm.targetKey]) this.resetDispute()
      if (!rowMap[this.bindForm.targetKey]) this.resetBindSystemCharge()
      this.persistDraftState(true)
    },
    pickNumber(...values) {
      for (const value of values) {
        if (value === null || value === undefined || value === '') continue
        const number = this.numberValue(value)
        if (Number.isFinite(number)) return number
      }
      return 0
    },
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    sameCurrency(left, right) {
      if (!left || !right) return false
      const normalizedLeft = String(left).trim().toUpperCase()
      const normalizedRight = String(right).trim().toUpperCase()
      if ((normalizedLeft === 'CNY' || normalizedLeft === 'RMB') &&
        (normalizedRight === 'CNY' || normalizedRight === 'RMB')) return true
      return normalizedLeft === normalizedRight
    },
    disputeAssigneeLabel(assignee) {
      const userId = assignee && assignee.userId
      const userName = userId
        ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(userId), 'chineseName')
        : ''
      const name = userName || `用户${userId || '-'}`
      return `${name}（${this.disputeAssigneeRoleLabel(assignee && assignee.roles)}）`
    },
    disputeAssigneeRoleLabel(roles) {
      const labels = {
        CHARGE_OWNER: '费用归属人',
        ORDER_OPERATOR: '订单操作人',
        BUSINESS_OWNER: '销售'
      }
      return String(roles || '').split(',').map(role => labels[role] || role).filter(Boolean).join(' / ') || '责任人'
    },
    systemChargeOptionLabel(item) {
      const name = item.chargeItemName || item.chargeItemId || '未命名费用'
      return `${name} ｜ ${this.formatMoney(item.amount, item.currency)}`
    },
    formatPlainAmount(value) {
      return this.numberValue(value).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    formatMoney(value, currency) {
      if (value === null || value === undefined || value === '') return '-'
      if (currency === 'CNY' || currency === 'USD') {
        return new Intl.NumberFormat('zh-CN', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(this.numberValue(value))
      }
      return this.formatPlainAmount(value)
    },
    formatDualAmount(cnyValue, usdValue) {
      const hasCny = cnyValue !== null && cnyValue !== undefined && this.numberValue(cnyValue) !== 0
      const hasUsd = usdValue !== null && usdValue !== undefined && this.numberValue(usdValue) !== 0
      if (hasCny && hasUsd) {
        return `${this.formatMoney(cnyValue, 'CNY')} / ${this.formatMoney(usdValue, 'USD')}`
      }
      if (hasCny) return this.formatMoney(cnyValue, 'CNY')
      if (hasUsd) return this.formatMoney(usdValue, 'USD')
      return this.formatMoney(0, 'CNY')
    }
  }
}
</script>

<style lang="scss" scoped>
.recon-compare-detail {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  padding-top: 6px;
  color: #303133;
  font-size: 12px;
  line-height: 20px;
}

.compare-alert {
  margin-bottom: 5px;
}

.compare-page,
.compare-page__main {
  min-height: 0;
}

.compare-page {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.compare-header,
.compare-subheader {
  min-height: 40px;
  flex-direction: row !important;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px !important;
}

.compare-header__meta {
  display: flex;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0 18px;
  border-right: 1px solid #dcdfe6;
  color: #909399;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: 0;
  }

  strong {
    color: #303133;
    font-size: 13px;
    font-weight: 600;
  }
}

.compare-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.compare-header__export {
  display: inline-flex;
}

.compare-header__save {
  flex-shrink: 0;
}

.compare-subheader {
  margin-bottom: 5px;
}

.compare-subheader__filters {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.compare-subheader__assignee {
  width: 165px;

  &.is-filtering {
    ::v-deep .el-input__inner {
      border-color: #1F75D5;
      background: #1F75D5;
      color: #fff;
    }

    ::v-deep .el-input__icon {
      color: #fff;
    }
  }
}

.compare-subheader__filter-hint {
  flex-shrink: 0;
  color: #1F75D5;
  font-size: 12px;
  line-height: 28px;
}

.compare-filters {
  flex-shrink: 0;
}

.compare-tip {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #909399;

  i {
    color: #409eff;
  }

  &.is-filtering {
    color: #1F75D5;

    i {
      color: #1F75D5;
    }
  }
}

.compare-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  margin-bottom: 5px;
  border-radius: 2px;
  background: #fff;
  color: #909399;

  i {
    margin-bottom: 8px;
    font-size: 28px;
  }
}

.record-card {
  overflow: hidden;
  margin-bottom: 5px;
  padding: 0 !important;
  border: 1px solid #ebeef5;
  border-left: 4px solid #67c23a;
  border-radius: 2px;
  background: #fff;
}

.record-card--alert {
  border-color: #fbc4c4;
  border-left-color: #f56c6c;
}

.record-card--invalid {
  border-color: #dcdfe6;
  border-left-color: #909399;
  background: #f5f7fa;
  opacity: 0.65;
}

.record-card--active {
  border-color: #409eff;
  box-shadow: 0 0 0 1px #409eff;
}

.record-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
}

.record-card__identity {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  i {
    color: #409eff;
    font-size: 14px;
  }

  strong {
    overflow: hidden;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.record-card__head-side {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  color: #909399;
  text-align: right;
}

.record-card__amount {
  font-weight: 700;
}

.record-card__amount--success {
  color: #67c23a;
}

.record-card__amount--danger {
  color: #f56c6c;
}

.record-card__summary {
  display: grid;
  grid-template-columns: 160px repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
}

.summary-item {
  min-width: 0;

  span,
  small {
    display: block;
    color: #909399;
  }

  strong {
    display: block;
    margin-top: 2px;
    color: #303133;
    font-size: 13px;
    font-weight: 600;
  }
}

.summary-item--title {
  color: #303133;
  font-size: 13px;
  font-weight: 700;
}

.summary-item--status {
  i {
    font-size: 14px;
    margin-right: 4px;
  }

  .is-success {
    color: #67c23a;
  }

  .is-warning {
    color: #e6a23c;
  }

  .is-danger {
    color: #f56c6c;
  }
}

.record-total-accept-tag,
.record-total-accept-btn {
  margin-top: 6px;
}

.record-table {
  padding: 4px 12px 8px;
}

.record-table__head,
.record-table__row {
  display: grid;
  grid-template-columns: minmax(140px, 1.1fr) minmax(120px, 0.8fr) minmax(140px, 1fr) minmax(120px, 0.8fr) 88px 136px minmax(248px, 1fr);
  column-gap: 12px;
  align-items: stretch;
}

.record-table__head {
  padding: 6px 8px;
  color: #909399;
  font-weight: 600;
}

.record-table__row {
  min-height: 40px;
  padding: 0 8px;
  border-top: 1px solid #ebeef5;
}

.record-table__row--active {
  background: #ecf5ff;
}

.record-table__cell {
  display: flex;
  align-items: center;
  min-height: 40px;
  color: #606266;
  word-break: break-word;
}

.record-table__cell--amount {
  font-variant-numeric: tabular-nums;
}

.record-table__cell--emphasis {
  color: #f56c6c;
  background: #fef0f0;
}

.manual-bind-tag {
  flex-shrink: 0;
  margin-left: 6px;
}

.bind-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bind-dialog__footer-actions {
  margin-left: auto;
}

.compare-dialog__empty {
  color: #f56c6c;
}

.record-table__cell--assignees {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  padding: 6px 0;
  line-height: 1.4;
}

.record-table__assignee {
  color: #606266;
}

.record-table__cell--actions {
  justify-content: flex-start;
  gap: 5px;

  ::v-deep .el-button + .el-button {
    margin-left: 0;
  }
}

.record-table__dash,
.record-table__empty,
.row-action-text {
  color: #909399;
}

.record-table__empty {
  padding: 12px 8px 6px;
}

.record-table__empty--invalid {
  padding: 16px 8px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.record-table__empty--invalid i {
  margin-right: 4px;
}

.row-status {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
}

.row-status--is-success {
  color: #67c23a;
}

.row-status--is-warning,
.row-status--warning,
.row-action-text--warning {
  color: #e6a23c;
}

.row-status--is-danger {
  color: #f56c6c;
}

.row-status--is-info,
.row-status--info,
.row-action-text--info {
  color: #409eff;
}

.compare-dialog__desc {
  margin-bottom: 14px;
  color: #606266;
}

.dispute-related {
  margin-bottom: 0;
}

.dispute-related__list {
  margin-top: 8px;
  max-height: 220px;
  padding: 8px 10px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f5f7fa;

  .el-checkbox {
    display: flex;
    align-items: flex-start;
    margin-right: 0;
    white-space: normal;
    line-height: 20px;
  }

  .el-checkbox + .el-checkbox,
  .el-checkbox-group .el-checkbox {
    margin-top: 6px;
  }

  ::v-deep .el-checkbox__label {
    white-space: normal;
    line-height: 20px;
  }
}

.dispute-related__toolbar {
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
}

.dispute-related__failures {
  margin-top: 10px;
  color: #f56c6c;
  font-size: 12px;
  line-height: 18px;
}

.dispute-related__failure {
  margin-top: 4px;
}

.compare-footer {
  position: relative;
  z-index: 2;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 0;
  padding: 8px 10px;
  border-top: 1px solid #dcdfe6;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, .04);
}

.compare-footer__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.compare-footer__label,
.compare-footer__diff {
  color: #909399;
  white-space: nowrap;
}

.compare-footer__progress-bar {
  width: 220px;
  max-width: 100%;
}

.compare-footer__diff strong {
  color: #f56c6c;
  font-size: 13px;
}

.compare-footer__actions {
  display: flex;
  align-items: center;
  gap: 6px;

  ::v-deep .el-button + .el-button {
    margin-left: 0;
  }
}

@media (max-width: 1100px) {
  .record-table {
    overflow-x: auto;
  }

  .record-table__head,
  .record-table__row {
    min-width: 1140px;
  }
}

@media (max-width: 768px) {
  .compare-header,
  .compare-subheader,
  .compare-footer {
    flex-direction: column !important;
    align-items: stretch;
  }

  .compare-header__meta,
  .record-card__head,
  .record-card__head-side {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .meta-item {
    padding: 0;
    border-right: 0;
  }

  .compare-subheader__filters,
  .compare-filters {
    width: 100%;
  }

  .compare-filters {
    overflow-x: auto;
  }

  .compare-subheader__assignee {
    width: 100%;
  }

  .record-card__summary {
    grid-template-columns: 1fr;
  }

  .compare-footer__progress-bar {
    width: 100%;
  }

  .compare-footer__actions {
    justify-content: flex-end;
  }
}
</style>
